import { inject, Injectable } from '@angular/core';
import { DEFAULT_TIMEZONE } from '@constants/date.constants';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { TimezoneDbModel } from '@db-models/timezone.model';
import { HttpClientService } from '@util-services/http-client.service';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { catchError, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {

  private httpClientService = inject(HttpClientService);

  getTzDate(value: string | Date, timezone?: string): moment.Moment {
    timezone = timezone || this.getTimezoneFromLocalStorage();
    return moment.utc(value).tz(timezone);
  }

  getTimezones(): Observable<TimezoneDbModel[]> {
    const timezone = this.getTimezoneListFromLocalStorage();
    if (timezone?.length) {
      return of(timezone);
    }
    return this.httpClientService.get(`timezones`, {}).pipe(
      catchError(() => of([])),
      switchMap((result: { success: boolean, data: TimezoneDbModel[] }) => {
        if (result?.data?.length) {
          this.setTimezoneListInLocalStorage(result?.data);
        }
        return of(result?.data);
      }),
    );
  }

  private getTimezoneListFromLocalStorage(): TimezoneDbModel[] {
    const timezone = localStorage.getItem(LOCAL_STORAGE_CONSTANT.TIMEZONE_LIST);
    return timezone ? JSON.parse(timezone) : [];
  }

  private setTimezoneListInLocalStorage(timezone: TimezoneDbModel[]): void {
    localStorage.setItem(LOCAL_STORAGE_CONSTANT.TIMEZONE_LIST, JSON.stringify(timezone));
  }

  setTimezoneInLocalStorage(timezone: string): void {
    localStorage.setItem(LOCAL_STORAGE_CONSTANT.TIMEZONE, timezone);
  }

  getTimezoneFromLocalStorage(): string {
    return localStorage.getItem(LOCAL_STORAGE_CONSTANT.TIMEZONE) || DEFAULT_TIMEZONE;
  }

  getTimezoneOffset(date: string | Date, timezone: string): string {
    !timezone && (timezone = this.getTimezoneFromLocalStorage());
    const offsetMinutes = moment(date).tz(timezone).utcOffset();
    const offsetHours = Math.floor(offsetMinutes / 60);
    const offsetMins = Math.abs(offsetMinutes % 60);
    const sign = offsetMinutes >= 0 ? '+' : '-';

    return `UTC${sign}${String(Math.abs(offsetHours)).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
  }

  getLocalTimezone(): string {
    let tz: string | null = null;

    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      tz = null;
    }

    // If Intl fails or returns nonsense, fallback to moment.tz.guess()
    if (!tz || tz.startsWith('Etc/')) {
      tz = momentTz.tz.guess(true);
    }

    return tz || 'Europe/Zurich';
  }

  getTimezoneLabel(zone: string): string {
    return this.getTimezoneListFromLocalStorage().find(timezone => timezone.zone_group.includes(zone))?.name;
  }
}
